import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import type { QueryClient } from '@tanstack/react-query';
import type { AccessTokenPayload } from '@/api/auth/interfaces';
import React, { Suspense } from 'react';
import { TenantSwitchProvider } from '@/contexts/TenantSwitchContext';

// Conditionally load TanStackRouterDevtools only in development for performance optimization
const TanStackRouterDevtools =
  process.env.NODE_ENV === 'production'
    ? () => null // No-op in production
    : React.lazy(() =>
        import('@tanstack/react-router-devtools').then((res) => ({
          default: res.TanStackRouterDevtools
        }))
      );

// Root route definition with context for QueryClient and Auth
export const Route = createRootRouteWithContext<{
  queryClient: QueryClient;
  auth: AccessTokenPayload | null;
}>()({
  validateSearch: (search) => {
    const result = { ...(search as Record<string, unknown>) };
    const rawClient = result.adminClientId;
    const rawCompany = result.adminCompanyId;
    const rawCustomer = result.adminCustomerId;

    if (typeof rawClient === 'string' && rawClient.trim().length > 0) {
      result.adminClientId = rawClient;
    } else {
      delete result.adminClientId;
    }
    if (typeof rawCompany === 'string' && rawCompany.trim().length > 0) {
      result.adminCompanyId = rawCompany;
    } else {
      delete result.adminCompanyId;
    }
    if (typeof rawCustomer === 'string' && rawCustomer.trim().length > 0) {
      result.adminCustomerId = rawCustomer;
    } else {
      delete result.adminCustomerId;
    }

    return result;
  },
  component: RootComponent
});

// Root component that chooses layout based on route and includes devtools
function RootComponent() {
  return (
    <TenantSwitchProvider>
      <Outlet />
      {/* React Query Devtools always available in dev */}
      <ReactQueryDevtools buttonPosition="bottom-left" />

      {/* TanStack Router Devtools only in development, lazy loaded for performance */}
      <Suspense fallback={null}>
        <TanStackRouterDevtools position="bottom-right" />
      </Suspense>
    </TenantSwitchProvider>
  );
}
